<template>
    <div
        class="l-ss-c-offer-image-container"
        ref="imgWrapper">
        <div
            class="l-ss-c-offer-image-wrapper"
            :class="{ 'l-ss-c-offer-video-wrapper': isVideo }">
            <template v-if="isVideo">
                <video
                    class="l-ss-c-offer-video"
                    :class="{ 'l-ss-c-offer-video-adult': isAdult }"
                    :src="currentMedia"
                    :style="style"
                    autoplay
                    muted
                    preload="none"
                    @error="errorVideoListener"
                    @loadeddata="onVideoLoadedData"
                    @ended="onVideoEnded"
                    ref="videoElement">
                    {{ $t('videoNotSupported') }}
                </video>
                <div
                    v-if="isVideoLoading"
                    class="l-ss-c-offer-video-skeleton"></div>
            </template>

            <img
                v-else
                class="l-ss-c-offer-image"
                :class="{ 'l-ss-c-offer-image-adult': isAdult }"
                :src="currentMedia"
                @error="errorImgListener"
                :style="style"
                ref="imgElement">

            <slot></slot>

            <div
                v-if="isAdult"
                class="l-ss-c-offer-image-adult-overlay">
                <i v-html="iconEyeSlash"></i>
                <span>Товар для взрослых</span>
            </div>
        </div>
        <div
            class="l-ss-c-offer-image-indicators"
            v-if="vertical || (!vertical && offerMedia.length > 1)">
            <template v-if="offerMedia.length > 1">
                <span
                    v-for="mediaIndex in offerMedia.length"
                    :key="mediaIndex"
                    ref="indicator"
                    :data-index="mediaIndex - 1"
                    class="l-ss-c-offer-image-indicator"
                    :class="{ 'l-ss-c-offer-image-indicator-active': currentMediaIndex === mediaIndex - 1 }"></span>
            </template>
        </div>
        <div
            v-if="isShowScore"
            class="l-ss-c-offer-image-score-overlay">
            <span>Score: {{ offer.score }}</span>
        </div>
    </div>
</template>

<script>
    import { ref, computed, watch } from '@vue/composition-api';

    import iconLike from '@/core/assets/images/icons/like.svg';
    import iconEyeSlash from '@/core/assets/images/icons/eye-slash.svg';

    import useImageController from '@/core/components/offer/offer-image/use-image-controller';

    export default {
        name: 'offer-image',
        props: {
            offer: {
                type: Object,
            },
            settings: {
                type: Object,
            },
            mobile: {
                type: Boolean,
                default: false,
            },
            vertical: {
                type: Boolean,
                default: false,
            },
            isAdult: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, { refs }) {
            const { settings } = props;
            const { style } = settings;
            const images = props.offer.pictures || [];

            const { errorImg: customErrorImg, labelsPosition, favouriteIconPosition } = settings;

            const maxImages = settings.max || 1;

            const offerMedia = ref([...images.slice(0, maxImages)]);

            const videoErrorIndexes = ref(new Set());

            let isLocalErrorImg = !customErrorImg;

            const getErrorImg = () => {
                if (isLocalErrorImg) {
                    return require('@/core/assets/images/no-photo.png');
                } else {
                    return customErrorImg;
                }
            };

            const isVideo = computed(() => {
                const index = currentMediaIndex.value;
                const mediaUrl = offerMedia.value[index];
                const isVideoFile = isVideoUrl(mediaUrl);

                return isVideoFile && !videoErrorIndexes.value.has(index);
            });

            const isShowScore = computed(() => {
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    if (urlParams.get('showScore') === 'true') return true;
                    if (urlParams.get('showScore') === 'false') return false;
                    
                    const hashParams = new URLSearchParams(window.location.hash.slice(1));
                    if (hashParams.get('showScore') === 'true') return true;
                    if (hashParams.get('showScore') === 'false') return false;
                    
                    const storageValue = localStorage.getItem('sb-showScore');
                    if (storageValue === 'true') return true;
                    if (storageValue === 'false') return false;
                } catch (error) {
                    console.error('Error checking showScore:', error);
                }

                return false;
            });

            const isVideoUrl = (url) => {
                const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.m4v', '.3gp'];
                const urlLower = url.toLowerCase();

                const hasVideoExtension = videoExtensions.some(ext => urlLower.endsWith(ext));

                return hasVideoExtension;
            };

            const createMediaCache = async () => {
                const mediaCache = [...offerMedia.value.slice(1)];

                const onErrorMedia = (i) => {
                    mediaCache[i] = getErrorImg();
                };

                const loadingMediaPromises = mediaCache.map((mediaForCache, i) => {
                    return new Promise((resolve) => {
                        if (isVideoUrl(mediaForCache)) {
                            resolve();
                        } else {
                            const image = new Image();
                            image.src = mediaForCache;

                            image.onload = resolve;

                            image.onerror = () => {
                                onErrorMedia(i);
                                resolve();
                            };
                        }
                    });
                });

                await Promise.all(loadingMediaPromises);

                offerMedia.value.splice(1, offerMedia.value.length - 1, ...mediaCache);
            };

            if (!offerMedia.value.length) {
                offerMedia.value.push(getErrorImg());
            } else if (offerMedia.value.length > 1) {
                createMediaCache();
            }

            const currentMediaIndex = useImageController(refs, offerMedia.value, props.mobile);

            const currentMedia = computed(() => {
                return offerMedia.value[currentMediaIndex.value] || getErrorImg();
            });

            watch(currentMediaIndex, () => {
                if (isVideo.value) {
                    const videoElement = refs.videoElement;
                    if (videoElement && videoElement.readyState === 0) {
                        videoElement.load();
                    }
                }
            });

            const errorVideoListener = (event) => {
                videoErrorIndexes.value.add(currentMediaIndex.value);
            };

            const errorImgListener = (event) => {
                event.target.src = getErrorImg();

                // Если нет кастомной картинки или она не загрузилась
                if (!isLocalErrorImg) {
                    isLocalErrorImg = true;
                }
            };

            const isVideoLoading = ref(true);

            function onVideoLoadedData() {
                isVideoLoading.value = false;
            }

            function onVideoEnded() {
                isVideoLoading.value = false;
                // Перезапускаем видео
                const videoElement = refs.videoElement;
                if (videoElement) {
                    videoElement.currentTime = 0;
                    videoElement.play();
                }
            }

            watch(isVideo, (newValue) => {
                if (newValue) isVideoLoading.value = true;
            });

            return {
                currentMediaIndex,
                currentMedia,
                iconLike,
                iconEyeSlash,
                offerMedia,
                videoErrorIndexes,
                labelsPosition,
                favouriteIconPosition,
                style,
                errorImgListener,
                errorVideoListener,
                isVideo,
                isVideoLoading,
                onVideoLoadedData,
                onVideoEnded,
                isShowScore,
            };
        },
    };
</script>
